import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSupabaseAuth } from '@/hooks/auth';
import { supabase } from '@/integrations/supabase/client';
import { getCourseRoute, getModuleRoute } from '@/utils/categoryRouteMapping';
import { useEffect } from 'react';

interface ModuleProgress {
  id: string;
  title: string;
  progress: number;
  hasQuiz?: boolean;
  quizPassed?: boolean;
}

interface EnrichedCourse {
  id: string;
  title: string;
  category: string;
  progress: number;
  completedModules: number;
  totalModules: number;
  modules: ModuleProgress[];
  lastAccessedAt?: Date;
  lastAccessedModuleId?: string;
  quizzesPassed: number;
  totalQuizzes: number;
  status: 'not_started' | 'in_progress' | 'completed';
  courseRoute: string;
}

/**
 * Optimized My Courses hook - uses batch RPC queries
 * Reduces N+1 queries to just 2-3 queries total
 */
export const useMyCourses = () => {
  const { user } = useSupabaseAuth();
  const queryClient = useQueryClient();

  // Real-time subscription for progress updates
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('my-courses-live-progress')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'user_module_progress',
        filter: `user_id=eq.${user.id}`
      }, () => {
        queryClient.invalidateQueries({ queryKey: ['my-courses-batch', user.id] });
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'course_assignments',
        filter: `user_id=eq.${user.id}`
      }, () => {
        queryClient.invalidateQueries({ queryKey: ['my-courses-batch', user.id] });
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient]);

  // Single batch query for courses with progress
  const { data: coursesData, isLoading: coursesLoading } = useQuery({
    queryKey: ['my-courses-batch', user?.id],
    queryFn: async () => {
      if (!user) return { courses: [], moduleProgress: [] };

      // Batch query 1: Get courses with progress using RPC
      const { data: coursesWithProgress, error } = await supabase
        .rpc('get_user_courses_with_progress', { p_user_id: user.id });

      if (error) {
        console.error('Error fetching courses:', error);
      }

      // Batch query 2: Get all module progress for user
      const { data: moduleProgress } = await supabase
        .from('user_module_progress')
        .select('module_id, course_id, progress, updated_at')
        .eq('user_id', user.id);

      return {
        courses: coursesWithProgress || [],
        moduleProgress: moduleProgress || [],
      };
    },
    enabled: !!user,
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  // Get modules for enrolled courses (single batch query)
  const { data: courseModulesMap = {} } = useQuery({
    queryKey: ['course-modules-map', coursesData?.courses?.length],
    queryFn: async () => {
      if (!coursesData?.courses?.length) return {};

      const courseIds = coursesData.courses.map((c: any) => c.course_id);
      
      // Single query for all modules
      const { data: allModules } = await supabase
        .from('course_modules')
        .select('course_id, module_id, module_title, module_order')
        .in('course_id', courseIds)
        .eq('is_enabled', true)
        .order('module_order');

      // Group by course_id
      const modulesMap: Record<string, any[]> = {};
      (allModules || []).forEach(m => {
        if (!modulesMap[m.course_id]) {
          modulesMap[m.course_id] = [];
        }
        modulesMap[m.course_id].push(m);
      });

      return modulesMap;
    },
    enabled: !!coursesData?.courses?.length,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  // Calculate enriched courses
  const enrichedCourses: EnrichedCourse[] = useMemo(() => {
    if (!coursesData?.courses) return [];

    return coursesData.courses.map((course: any) => {
      const modules = courseModulesMap[course.course_id] || [];
      const courseModuleProgress = (coursesData.moduleProgress || []).filter(
        (p: any) => p.course_id === course.course_id
      );

      // Build modules array with progress
      const modulesWithProgress: ModuleProgress[] = modules.map((module: any) => {
        const progress = courseModuleProgress.find((p: any) => p.module_id === module.module_id);
        return {
          id: module.module_id,
          title: module.module_title,
          progress: progress?.progress || 0,
          hasQuiz: true,
          quizPassed: (progress?.progress || 0) === 100,
        };
      });

      // Find last accessed module
      const sortedProgress = [...courseModuleProgress].sort((a: any, b: any) => 
        new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      );
      const lastAccessedModuleId = sortedProgress[0]?.module_id || modules[0]?.module_id;

      // Determine status - use >= 100 to handle any edge cases
      let status: 'not_started' | 'in_progress' | 'completed' = 'not_started';
      const rawProgress = course.overall_progress || 0;
      const totalModules = Number(course.total_modules) || 0;
      const completedModules = Math.min(Number(course.completed_modules) || 0, totalModules);
      
      // Cap progress at 100%
      const progress = Math.min(rawProgress, 100);
      
      if (progress >= 100 || (totalModules > 0 && completedModules >= totalModules)) {
        status = 'completed';
      } else if (progress > 0) {
        status = 'in_progress';
      }

      return {
        id: course.course_id,
        title: course.course_title,
        category: course.course_category,
        progress,
        completedModules,
        totalModules,
        modules: modulesWithProgress,
        lastAccessedAt: course.last_activity_date ? new Date(course.last_activity_date) : undefined,
        lastAccessedModuleId,
        quizzesPassed: Number(course.completed_modules) || 0,
        totalQuizzes: Number(course.total_modules) || 0,
        status,
        courseRoute: getCourseRoute(course.course_id, course.course_category),
      };
    });
  }, [coursesData, courseModulesMap]);

  // Sort by progress (highest first)
  const sortedCourses = useMemo(() => {
    return [...enrichedCourses].sort((a, b) => b.progress - a.progress);
  }, [enrichedCourses]);

  // Get continue learning course
  const continueLearningCourse = useMemo(() => {
    const inProgress = sortedCourses.filter(c => c.status === 'in_progress');
    if (inProgress.length === 0) {
      return sortedCourses.find(c => c.status === 'not_started') || null;
    }
    return inProgress.sort((a, b) => {
      const aTime = a.lastAccessedAt?.getTime() || 0;
      const bTime = b.lastAccessedAt?.getTime() || 0;
      return bTime - aTime;
    })[0] || null;
  }, [sortedCourses]);

  // Stats
  const stats = useMemo(() => ({
    totalCourses: enrichedCourses.length,
    inProgress: enrichedCourses.filter(c => c.status === 'in_progress').length,
    completed: enrichedCourses.filter(c => c.status === 'completed').length,
    notStarted: enrichedCourses.filter(c => c.status === 'not_started').length,
    totalModulesCompleted: enrichedCourses.reduce((acc, c) => acc + c.completedModules, 0),
    overallProgress: enrichedCourses.length > 0
      ? Math.round(enrichedCourses.reduce((acc, c) => acc + c.progress, 0) / enrichedCourses.length)
      : 0,
  }), [enrichedCourses]);

  // Helper to get module route
  const getModuleRouteForCourse = (courseId: string, moduleId: string, category?: string) => {
    return getModuleRoute(courseId, moduleId, category);
  };

  return {
    courses: sortedCourses,
    continueLearningCourse,
    stats,
    loading: coursesLoading,
    getModuleRouteForCourse,
  };
};

export default useMyCourses;
